/**
 * The convoy: merchant hulls that one team escorts across the chart and the
 * other team sinks.
 *
 * ── The shape of it ─────────────────────────────────────────────────────────
 *
 * Team A's line forms up as usual, and behind it three merchantmen steam for
 * a port behind team B's line. A merchant is unarmed, slow, easy to see and
 * hard to sink; it holds its course for the port and goes round land, and
 * that is all it knows. A merchant that reaches the port pays team A; a
 * merchant on the bottom pays team B the same. Three merchants at 250 each
 * against a cap of 750 means the convoy decides the match unless the clock
 * or a wiped line gets there first, and a mixed result — two home, one sunk —
 * ends the battle on points when the last merchant is resolved, so nobody
 * waits out a clock with nothing left to fight for.
 *
 * The escorts and the raiders are the same bots as everywhere else. What
 * changes is where they keep station: the convoy hands both lines the living
 * merchants as the "rings" to sail for (`Arena.convoyStations`), so A's bots
 * stay with the ships they are guarding and B's bots go looking for them.
 * Nothing in `bot-ai.ts` knows the word merchant.
 *
 * Pure: the geometry, the merchant's helm and the spec. The arena owns the
 * merchants and the scoring.
 */
import { bearingTo, keepInside, steerTo } from './bot-ai';
import { type IslandLayout, type SpawnLine, avoidLand } from './islands';
import type { ZoneLayout } from './objectives';
import type { ShipSpec } from './ship-spec';
import { ARENA_HALF, type ShipInput, type ShipState } from './simulation';

export interface ConvoyRules {
  /** Merchants in the convoy. */
  count: number;
  /** Points to A for one home, to B for one sunk. */
  pointsPerMerchant: number;
  /** Metres from the port's centre at which a merchant is home. */
  arrivalRadius: number;
}

export const CONVOY: ConvoyRules = { count: 3, pointsPerMerchant: 250, arrivalRadius: 350 };

/**
 * The merchantman. A cruiser's hull for the renderer and the hit box, and
 * nothing on its deck: no guns, no tubes. Slower than anything that hunts it
 * (12 m/s against a destroyer's 18), seen from further than anything else on
 * the chart (4 km), and 32,000 hit points that saturate at the ends like any
 * other hull — a raider that wants it down has to close and hit the citadel,
 * under the escorts' guns, which is the whole trade of the mode.
 */
export const MERCHANT_SPEC: ShipSpec = {
  id: 'merchant',
  classId: 'cruiser',
  modelId: 'cruiser',
  name: 'Merchantman',
  lengthM: 135,
  beamM: 18,
  maxSpeed: 12,
  astern: 0.3,
  accel: 0.5,
  decel: 0.9,
  turnRate: 0.09,
  rudderResponse: 0.7,
  hp: 32_000,
  concealment: 4_000,
  hull: { halfLength: 67.5, halfBeam: 9, draft: 6, height: 9 },
  turrets: [],
};

export const MERCHANT_NAMES: readonly string[] = ['Anselm Star', 'Bright Meridian', 'Coral Trader', 'Dunmore Castle', 'Ellery Rose', 'Fairwind'];

/** Forward unit vector of a heading, like a ship's. */
function forward(yaw: number): { x: number; z: number } {
  return { x: Math.sin(yaw), z: Math.cos(yaw) };
}

/** Clear water kept between the port's edge and the arena wall. */
const PORT_WALL_MARGIN = 100;

/**
 * The port: 500 m behind team B's line, straight down its facing, or as far
 * behind as the wall allows (the atoll's lines stand near it). The merchants
 * have to pass through B's line to get there, which is what makes B's line
 * the place to be for both sides. Drawn as a ring on the chart, so it is
 * shaped like one; it is not a capture ring and nothing steps it.
 */
export function convoyPort(layout: IslandLayout, rules: ConvoyRules = CONVOY): ZoneLayout {
  const line = layout.lines.B;
  const f = forward(line.yaw);
  const reach = ARENA_HALF - PORT_WALL_MARGIN - rules.arrivalRadius;
  const clamp = (v: number): number => Math.max(-reach, Math.min(reach, v));
  return { id: 'port', x: clamp(line.x - f.x * 500), z: clamp(line.z - f.z * 500), radius: rules.arrivalRadius, near: 'B', weight: 0 };
}

/**
 * Where merchant `i` of `count` starts: 450 m behind team A's line, spread
 * across the line's facing 300 m apart, the middle one on the centreline.
 * Behind the line rather than in it, so the escorts are between the convoy
 * and the enemy from the first tick.
 */
export function merchantSpawn(layout: IslandLayout, i: number, count: number): { x: number; z: number; yaw: number } {
  const line: SpawnLine = layout.lines.A;
  const f = forward(line.yaw);
  const across = (i - (count - 1) / 2) * 300;
  // Perpendicular to the heading (sin yaw, cos yaw) is (cos yaw, −sin yaw).
  return { x: line.x - f.x * 450 + Math.cos(line.yaw) * across, z: line.z - f.z * 450 - Math.sin(line.yaw) * across, yaw: line.yaw };
}

/** True when the merchant is inside the port. */
export function merchantHome(state: ShipState, port: ZoneLayout): boolean {
  const dx = state.x - port.x;
  const dz = state.z - port.z;
  return dx * dx + dz * dz <= port.radius * port.radius;
}

/**
 * A merchant's helm: full ahead for the port, round the land, and back inside
 * the walls if it ever strays. It never aims, never fires and never repairs;
 * a merchant on fire burns until the fire goes out on its own.
 */
export function decideMerchant(state: ShipState, spec: ShipSpec, layout: IslandLayout, port: ZoneLayout): ShipInput {
  const wanted = bearingTo(state, port.x, port.z);
  const clear = avoidLand(layout, state.x, state.z, wanted, 700 + Math.abs(state.speed) * 30, spec.beamM * 0.5 + 40);
  const bearing = keepInside(state, clear);
  return { throttle: 1, rudder: steerTo(state, bearing), aimBearing: state.yaw, aimRange: 1000, fire: 0, fireTorpedoes: 0, repair: 0 };
}
